/**
 * Lo que se escribe en cada formato oficial.
 *
 * ⚠ **Los campos son TEXTO, incluidos los que parecen fechas y montos.** Un formato
 * de aduana se rellena como se rellena a mano: el día puede ir «03» o «3», el valor
 * CIF puede llevar el símbolo, y el año de un vehículo a veces se escribe «2019/2020»
 * porque así venía en la factura. Validarlos aquí obligaría a rechazar lo que el
 * funcionario acepta, y el sistema no es quien decide qué admite la aduana.
 *
 * ⚠ **`DatosPosicionamiento` tiene espejo en el backend: `schemas/impresiones.py`.**
 * Cada vez que se imprime la solicitud se guarda lo tecleado junto a su PDF, y el
 * servidor lo valida con una lista blanca estricta. Un campo que se añada aquí y no
 * allí hace que imprimir responda 422 y no salga el papel — que es el fallo correcto:
 * sin registro no hay impresión. `DatosVehiculos` sigue sin espejo porque ese formato
 * todavía no se registra.
 */

export type TipoPlantilla = "POSICIONAMIENTO" | "VEHICULOS" | "GUIA_DESPACHO"

/**
 * Los formatos cuya impresión se REGISTRA con su PDF.
 *
 * ⚠ **Es un subconjunto a propósito, y el tipo lo dice.** El `ENUM` de
 * `impresiones_formato` sólo conoce estos dos; escribir `TipoPlantilla` donde va un
 * formato registrado dejaría compilar un `formato: "GUIA_DESPACHO"` que la base
 * rechazaría en tiempo de ejecución. También es lo que hace que `PIE` —que sólo tiene
 * las coordenadas de los dos formatos de la aduana— no acepte una guía por descuido.
 */
export type FormatoRegistrado = "POSICIONAMIENTO" | "VEHICULOS"

/** Las ocho casillas de régimen de la cabecera. */
export type Regimen = "ENA" | "RE" | "IMA" | "AT" | "IM" | "ID" | "RI" | "TR"

export type Fecha = { dia: string; mes: string; anio: string }

/** La cabecera y el pie son iguales en los dos formatos. */
export type Comun = {
  regimenes: Record<Regimen, boolean>
  fecha_solicitud: Fecha
  responsable: { nombre: string; telefono: string }
}

export type DatosPosicionamiento = Comun & {
  tipo: "POSICIONAMIENTO"
  dua: string
  bl: string
  agencia: string
  registro: string
  fecha_reconocimiento: Fecha
  funcionario: string
  color: string
  almacen: string
  codigo: string
  mercancia: string
  contenedor: boolean
  carga_suelta: boolean
  cantidad: string
  /** Tres renglones, porque el formato tiene tres y la nota dice qué hacer con más. */
  siglas: [string, string, string]
}

export type DatosVehiculos = Comun & {
  tipo: "VEHICULOS"
  declarante: string
  consignatario: string
  dua: string
  fecha: string
  valor_cif: string
  tipo_cambio: string
  valor_aduana: string
  marca: string
  modelo: string
  anio: string
  cantidad: string
}

/**
 * La guía de despacho: el papel con el que sale la mercancía del puerto.
 *
 * ⚠ **Es el ÚNICO formato de la biblioteca que es NUESTRO.** Los otros dos son de la
 * Aduana Principal de Puerto Cabello y se reproducen al punto; éste lo redacta la
 * agencia, lo firma la agencia y lo entrega al transportista. Por eso no hereda
 * `Comun`: no tiene casillas de régimen —que son del SENIAT— ni el bloque de
 * responsable del formato oficial.
 *
 * ⚠ **Nada se valida: son textos.** «01x40» es una cantidad, «N/A» es un precinto y
 * la fecha de llegada va pegada al nombre del buque porque así se lee en el papel.
 * Partirlo en campos tipados obligaría a teclear en cuatro casillas lo que hoy se
 * escribe en una.
 */
export type DatosGuia = {
  tipo: "GUIA_DESPACHO"
  /** El correlativo que lleva la agencia. No lo pone el sistema: ver `plantillaEnBlanco`. */
  numero: string
  lugar: string
  /** En prosa, como se escribe en una carta: «2 de octubre de 2026». */
  fecha: string
  expediente: string
  buque: string
  cliente: string
  bl: string
  rif: string
  mercancia: string
  direccion: string
  carga: { cantidad: string; contenedor: string; destino: string }
  conductor: { nombre: string; cedula: string; telefono: string }
  vehiculo: { modelo: string; color: string; placa: string }
}

export type DatosPlantilla = DatosPosicionamiento | DatosVehiculos | DatosGuia

const SIN_MARCAR: Record<Regimen, boolean> = {
  ENA: false,
  RE: false,
  IMA: false,
  AT: false,
  IM: false,
  ID: false,
  RI: false,
  TR: false,
}

/**
 * Una plantilla en blanco, con la fecha de hoy puesta.
 *
 * ⚠ **La fecha de solicitud nace rellena y el resto no.** Es el único campo del que
 * el sistema sabe la respuesta con certeza —es hoy— y es también el que más se olvida
 * al rellenar a mano. Precargar cualquier otro sería adivinar.
 */
const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

/** La fecha en prosa, que es como se encabeza una guía: «2 de octubre de 2026». */
export function enProsa(fecha: Date): string {
  return `${fecha.getDate()} de ${MESES[fecha.getMonth()]} de ${fecha.getFullYear()}`
}

export function plantillaEnBlanco(tipo: TipoPlantilla, hoy = new Date()): DatosPlantilla {
  if (tipo === "GUIA_DESPACHO") {
    return {
      tipo,
      numero: "",
      // ⚠ **El lugar nace puesto y el número NO.** La agencia despacha desde Puerto
      // Cabello y eso no cambia de una guía a otra; el correlativo lo lleva ella en su
      // propio control, y el sistema todavía no lo emite. Inventarlo sería numerar un
      // documento que se entrega con un número que no le corresponde a nadie.
      lugar: "Puerto Cabello",
      fecha: enProsa(hoy),
      expediente: "",
      buque: "",
      cliente: "",
      bl: "",
      rif: "",
      mercancia: "",
      direccion: "",
      carga: { cantidad: "", contenedor: "", destino: "" },
      conductor: { nombre: "", cedula: "", telefono: "" },
      vehiculo: { modelo: "", color: "", placa: "" },
    }
  }

  const comun: Comun = {
    regimenes: { ...SIN_MARCAR },
    fecha_solicitud: {
      dia: String(hoy.getDate()).padStart(2, "0"),
      mes: String(hoy.getMonth() + 1).padStart(2, "0"),
      anio: String(hoy.getFullYear()),
    },
    responsable: { nombre: "", telefono: "" },
  }

  if (tipo === "POSICIONAMIENTO") {
    return {
      ...comun,
      tipo,
      dua: "",
      bl: "",
      agencia: "",
      registro: "",
      fecha_reconocimiento: { dia: "", mes: "", anio: "" },
      funcionario: "",
      color: "",
      almacen: "",
      codigo: "",
      mercancia: "",
      contenedor: false,
      carga_suelta: false,
      cantidad: "",
      siglas: ["", "", ""],
    }
  }
  return {
    ...comun,
    tipo,
    declarante: "",
    consignatario: "",
    dua: "",
    fecha: "",
    valor_cif: "",
    tipo_cambio: "",
    valor_aduana: "",
    marca: "",
    modelo: "",
    anio: "",
    cantidad: "",
  }
}

/**
 * El título tal como lo imprime el formato.
 *
 * ⚠ **«Solicitud de Posicionamiento:» lleva sus dos puntos.** Están en el PDF y no
 * sobran: es el título del documento oficial, no un encabezado nuestro. Para el
 * selector de la pantalla hay otra lista, porque ahí sí es una etiqueta nuestra.
 */
export const NOMBRES: Record<TipoPlantilla, string> = {
  POSICIONAMIENTO: "Solicitud de Posicionamiento:",
  VEHICULOS: "Vehículos Confrontados 2026",
  // ⚠ **Con tilde, al revés que los otros dos.** Las faltas de un formato oficial se
  // conservan porque el documento no es nuestro; éste sí lo es, y la papelería de la
  // agencia se escribe bien. El original en Word decía «GUIA», «DIRECCION» y «CEDULA».
  GUIA_DESPACHO: "Guía de Despacho",
}

/** Cómo se nombra cada formato en el selector de la pantalla. */
export const ETIQUETAS: Record<TipoPlantilla, string> = {
  POSICIONAMIENTO: "Solicitud de Posicionamiento",
  VEHICULOS: "Vehículos Confrontados 2026",
  GUIA_DESPACHO: "Guía de Despacho",
}

/**
 * El papel de cada formato, en milímetros.
 *
 * ⚠ **No todos son iguales, y por eso están aquí y no en un `const` dentro de la
 * hoja.** Los dos de la aduana son A4; la guía es CARTA, como el documento de Word
 * del que sale y como el resto de lo que imprime la agencia. Cada medida arrastra su
 * `@page` con nombre en `globales.css`: con el papel equivocado, una hoja se parte en
 * dos y la otra sale con una página en blanco detrás.
 */
export const PAPEL: Record<TipoPlantilla, { ancho: number; alto: number }> = {
  POSICIONAMIENTO: { ancho: 210, alto: 297 },
  VEHICULOS: { ancho: 210, alto: 297 },
  GUIA_DESPACHO: { ancho: 216, alto: 279 },
}

/** Una impresión registrada, tal como la lista el servidor (`ImpresionOut`). */
export type Impresion = {
  id: number
  formato: FormatoRegistrado
  creado_en: string
  creado_por: number
  creado_por_nombre: string | null
  archivo_bytes: number
  dua: string
  bl: string
  agencia: string
}

export type PaginaImpresiones = {
  total: number
  limit: number
  offset: number
  items: Impresion[]
}
